import { uniqueBy } from '@toggle/helpers';

import { config } from '~/config';

import { useBaseWs } from '~/services/use-base-ws/useBaseWs';
import {
  CloseReason,
  NORMAL_CLOSURE,
  WebSocketStatus,
} from '~/services/use-base-ws/utils';
import {
  ErrorHandler,
  PriceWsAction,
  PriceWsEvent,
  PriceUpdate,
  SocketResponses,
  TickerPayload,
  UpdateHandler,
} from './types';

const MAX_IDLE_TIME = 60000;

export type PriceWsProps = {
  updateHandler: UpdateHandler;
  delayedPrices?: boolean;
  onError?: ErrorHandler;
  getSubscribedTickers: () => TickerPayload[];
};

export interface PriceWSInstance {
  subscribe: (tickers: TickerPayload[]) => void;
  unsubscribe: (tickers: TickerPayload[]) => void;
  disconnect: (code?: number, reason?: CloseReason) => void;
  readonly status: WebSocketStatus;
}

export const createPriceWS = ({
  updateHandler,
  delayedPrices,
  onError,
  getSubscribedTickers,
}: PriceWsProps): PriceWSInstance => {
  const {
    api: { wsRoot, wsDelayedRoot },
  } = config;
  const url = delayedPrices ? wsDelayedRoot : wsRoot;

  let closeTimer: number | undefined;

  const handleMessage = (payload: SocketResponses) => {
    const { event, ...pricePayload } = payload;

    switch (event) {
      case PriceWsEvent.PRICE:
        updateHandler(pricePayload as PriceUpdate);
        break;
      case PriceWsEvent.ERROR:
        onError?.(payload.message);
        break;
      case PriceWsEvent.SUBSCRIBED:
      case PriceWsEvent.UNSUBSCRIBED:
        break;
      default:
        onError?.(`Unknown event: ${event}`);
    }
  };

  const subscribeTickers = (tickers: TickerPayload[]) => {
    tickers.forEach(({ ticker, trading_country }) =>
      base.sendMessage(
        JSON.stringify({
          action: PriceWsAction.SUBSCRIBE,
          ticker,
          trading_country,
        })
      )
    );
  };

  const onOpen = () => {
    const tickers = uniqueBy(
      getSubscribedTickers(),
      t => `${t.ticker}|${t.trading_country}`
    );
    subscribeTickers(tickers);
  };

  const onClose = (event: CloseEvent) => {
    clearTimeout(closeTimer);
    closeTimer = undefined;
    if (event.reason === CloseReason.NoConnectionRequired) {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    }
  };

  const scheduleClose = (reason: CloseReason) => {
    closeTimer = window.setTimeout(
      () => base.disconnect(NORMAL_CLOSURE, reason),
      MAX_IDLE_TIME
    ) as unknown as number;
  };

  const onVisibilityChange = () => {
    window.clearTimeout(closeTimer);

    const isAlive =
      base.status !== WebSocketStatus.CLOSED &&
      base.status !== WebSocketStatus.UNINSTANTIATED;

    if (document.hidden && isAlive) {
      scheduleClose(CloseReason.InactiveTab);
    } else if (!isAlive) {
      base.connect();
    }
  };

  const base = useBaseWs(url, {
    onOpen: () => onOpen(),
    onClose: (event: CloseEvent) => onClose(event),
    onMessage: (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data) as SocketResponses;
        handleMessage(data);
      } catch (_error) {
        onError?.('WebSocket: Failed to parse message');
      }
    },
    onError: () => onError?.('WebSocket: Transport error'),
    shouldReconnect: () => true,
    reconnectAttempts: 5,
  });

  document.addEventListener('visibilitychange', onVisibilityChange);

  return {
    subscribe: (tickers: TickerPayload[]) => {
      window.clearTimeout(closeTimer);

      const uniqueTickers = uniqueBy(
        tickers,
        t => `${t.ticker}|${t.trading_country}`
      );
      if (base.isConnected && uniqueTickers.length > 0) {
        subscribeTickers(uniqueTickers);
      }
    },
    unsubscribe: (tickers: TickerPayload[]) => {
      if (!base.isConnected) {
        return;
      }

      const uniqueTickers = uniqueBy(
        tickers,
        t => `${t.ticker}|${t.trading_country}`
      );
      uniqueTickers.forEach(({ ticker, trading_country }) =>
        base.sendMessage(
          JSON.stringify({
            action: PriceWsAction.UNSUBSCRIBE,
            ticker,
            trading_country,
          })
        )
      );

      if (getSubscribedTickers().length === 0) {
        scheduleClose(CloseReason.NoConnectionRequired);
      }
    },
    disconnect: (code?: number, reason?: CloseReason) => {
      base.disconnect(code, reason);
    },
    get status() {
      return base.status;
    },
  };
};
